import React from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	root: {
		height: '100%',
		width: '100%',
		boxSizing: 'border-box',
		overflowY: 'auto',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.250'),
		marginTop: token('space.250'),
		marginBottom: token('space.250'),
		marginLeft: token('space.250'),
		marginRight: token('space.250'),
		paddingTop: token('space.250'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	pillsContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.050'),
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	artefactRow: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		minHeight: '64px',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
});

const PILL_CONTENT_WIDTHS = [120, 88, 162, 65, 128];

export const SessionRightPanelSkeleton = (): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.root}>
			<Box xcss={styles.content}>
				<Box xcss={styles.section}>
					<Heading size="xsmall">{formatMessage(messages.attachmentsHeading)}</Heading>
					<Box xcss={styles.pillsContainer}>
						{PILL_CONTENT_WIDTHS.map((width) => (
							<Box key={width} xcss={styles.pill}>
								<Skeleton width={width} height={14} isShimmering />
							</Box>
						))}
					</Box>
				</Box>
				<Box xcss={styles.section}>
					<Heading size="xsmall">{formatMessage(messages.outputHeading)}</Heading>
					<Stack space="space.050">
						{([150, 120, 160] as const).map((titleWidth) => (
							<Box key={titleWidth} xcss={styles.artefactRow}>
								<Skeleton
									width={32}
									height={32}
									borderRadius={token('radius.small')}
									isShimmering
								/>
								<Stack space="space.025">
									<Skeleton width={titleWidth} height={14} isShimmering />
									<Skeleton width={100} height={12} isShimmering />
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
			</Box>
		</Box>
	);
};
